/// <reference path='../DataTypes/types.js' />

import SettingsService from './SettingsService';
import DateService from './DateService';
import AuthenticationService from './AuthenticationService';

export function addVat(price) {
  return price * (1 + SettingsService.getVatRate());
}

export function addVatByRoom(room) {
  const price = SettingsService.getUseDailyRates()
    ? room.SelfStoreDayPrice
    : room.SelfStoreMonthPrice;
  return price * (1 + SettingsService.getVatRate());
}

export function removeVat(price) {
  return price / (1 + SettingsService.getVatRate());
}

export function formatPrice(price, language) {
    return price.toLocaleString(language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatPriceByRoom(room, language) {
  // format price based on daily or monthly rate
  var price = SettingsService.getUseDailyRates()
    ? room.SelfStoreDayPrice
    : room.SelfStoreMonthPrice;
  if (price == null) return '';
  return price.toLocaleString(language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}


const PaymentService = {
  calculateProratedAmount: (room, dateFrom) => {
    // Use monthly or daily rate based on settings
    const monthlyPrice = room.SelfStoreMonthPrice;
    const dailyPrice = room.SelfStoreDayPrice;

    const startDate = new Date(dateFrom);
    const daysInMonth = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      0
    ).getDate();
    const daysInNextMonth = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 2,
      0
    ).getDate();
    const remainingDays =
      PaymentService.calculateProratedAmountInDays(dateFrom);

    if (SettingsService.getUseDailyRates()) {
      const thisMonthAmount = remainingDays * dailyPrice;
      const nextMonthAmount = daysInNextMonth * dailyPrice;
      return thisMonthAmount + nextMonthAmount;
    }

    const dailyRate = monthlyPrice / daysInMonth;
    return dailyRate * remainingDays + monthlyPrice;
  },


  calculateProratedAmountOnly: (room, dateFrom) => {
    const startDate = new Date(dateFrom);
    const daysInMonth = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      0
    ).getDate();
    const remainingDays =
      PaymentService.calculateProratedAmountInDays(dateFrom);
    
    const dailyPrice = room.SelfStoreDayPrice;
    const monthlyPrice = room.SelfStoreMonthPrice;
    if (SettingsService.getUseDailyRates()) {
      return dailyPrice * remainingDays;
    }
  
    const dailyRate = monthlyPrice / daysInMonth;
    return dailyRate * remainingDays;
  },

  calculateProratedAmountInDays: dateFrom => {
    const startDate = new Date(dateFrom);
    const daysInMonth = new Date(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      0
    ).getDate();
    const dayOfMonth = startDate.getDate();
    return daysInMonth - dayOfMonth + 1;
  },

  async getOrderFinalizationData(
    paymentIntentId,
    orderId,
    invoiceId,
    countryShortName
  ) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/GetOrderFinalizationSessionData`,
        {
          method: 'POST',
          body: JSON.stringify({
            PaymentIntentId: paymentIntentId,
            OrderId: orderId,
            InvoiceId: invoiceId,
            CountryShortName: countryShortName,
          }),
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create checkout session');
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  },

  async createCheckoutSession({ orderId, user, date, currentLocation }) {
    /** @type {RentSelfStorageContainerRequest} */
    const data = {
      orderId,
      countryShortName: SettingsService.getCountryShortCode(),
      locationName: currentLocation.name,
      dateFrom: DateService.formatDate(date),
      userEmail: user.email,
      userId: user.stripeCustomerId,
    };

    try {
      const token = AuthenticationService.getToken();
      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/CreateCheckoutSession`,
        {
          method: 'POST',
          body: JSON.stringify(data),
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create checkout session');
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  },

  /**
   * @param {CreateStripeCustomerRequest} user
   * @returns {Promise<CreateStripeCustomerResponse>}
   */
  async createStripeCustomer(user) {
    try {
      const token = AuthenticationService.getToken();

      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/CreateStripeCustomer`,
        {
          method: 'POST',
          body: JSON.stringify({
            UserEmail: user.email,
            UserName: `${user.firstName} ${user.lastName}`,
            Phone: user.phone,
            Country: user.address?.country,
            CountryShortName: SettingsService.getCountryShortCode(),
            City: user.address?.city,
            Line1: user.address?.streetAddress,
            State: user.address?.state,
            PostalCode: user.address?.zipCode,
          }),
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create Stripe customer');
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  },

  async createVerificationSession(
    userEmail,
    titanEmail,
    rsItemId,
    dateFrom,
    locationName,
    stripeUserId
  ) {
    try {
      const countryShortName = SettingsService.getCountryShortCode();
      const token = AuthenticationService.getToken();

      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/VerifyUserDocumentation`,
        {
          method: 'POST',
          body: JSON.stringify({
            UserEmail: userEmail,
            TitanEmail: titanEmail,
            StripeCustomerId: stripeUserId,
            CountryShortName: countryShortName,
            RsItemId: rsItemId,
            DateFrom: dateFrom,
            LocationName: locationName,
          }),
          headers: { 'Content-Type': 'application/json' },
          ...(token && { Authorization: `Bearer ${token}` }),
        }
      );

      let responseData = await response.json();
      if (!response.ok) {
        const errorText =
          responseData?.message || 'Failed to create verification session';
        throw new Error(errorText);
      }
      return {
        clientSecret: responseData.clientSecret,
        verificationSessionId: responseData.verificationSessionId,
      };
    } catch (error) {
      throw error;
    }
  },

  async checkVerificationSessionStatus(verificationSessionId, userEmail) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/CheckVerifyUserIdentityStatus`,
        {
          method: 'POST',
          body: JSON.stringify({
            CountryShortName: SettingsService.getCountryShortCode(),
            VerificationSessionId: verificationSessionId,
            UserEmail: userEmail,
          }),
          headers: { 'Content-Type': 'application/json' },
        }
      );

      return response.json();
    } catch (error) {
      throw error;
    }
  },

  /**
   * @param {CreateDebtorRequest} user
   * @returns {Promise<UnicontaDebtorResponse>}
   */
  async GetOrCreateDebtorAccount(user) {
    try {
      const token = AuthenticationService.getToken();

      if (!token)
        throw new Error(
          'User is not authenticated so we cannot create a debtor account'
        );

      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/CreateDebtor`,
        {
          method: 'POST',
          body: JSON.stringify(user),
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create debtor');
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  },

  /**
   * @param {CreateOrderBookingRequest} bookingRequest
   * @returns {Promise<CreateBookingOrderResponse>}
   */
  async CreateBookingOrder(bookingRequest) {
    try {
      const token = AuthenticationService.getToken();

      if (!token)
        throw new Error(
          'User is not authenticated so we cannot create a booking order'
        );

      const response = await fetch(
        `${process.env.REACT_APP_CONSUMER_API_URL}/api/CheckoutApi/CreateBookingOrder`,
        {
          method: 'POST',
          body: JSON.stringify(bookingRequest),
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create booking order');
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  },
};

export default PaymentService;
